import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'

type Transferencia = {
  id: number
  titulo: string | null
  jogador_nome: string
  posicao: string | null
  clube_origem: string | null
  clube_origem_escudo: string | null
  clube_destino: string | null
  clube_destino_escudo: string | null
  status: string
  imagem_jogador: string | null
  destaque: boolean | null
  ativo: boolean | null
  ordem: number | null
  resumo: string | null
  link_noticia: string | null
  cor_status: string | null
  data_movimentacao: string | null
  criado_em: string | null
}

const statusOptions = [
  { label: 'Negociação avançada', color: '#eab308' },
  { label: 'Sondagem', color: '#3b82f6' },
  { label: 'Contratado', color: '#22c55e' },
  { label: 'Proposta recusada', color: '#ef4444' },
]

const emptyForm = {
  titulo: '',
  jogador_nome: '',
  posicao: '',
  clube_origem: '',
  clube_origem_escudo: '',
  clube_destino: '',
  clube_destino_escudo: '',
  status: 'Sondagem',
  imagem_jogador: '',
  destaque: false,
  ativo: true,
  ordem: 0,
  resumo: '',
  link_noticia: '',
  cor_status: '#3b82f6',
  data_movimentacao: new Date().toISOString().slice(0, 10),
}

export default function MercadoDaBolaManager() {
  const [items, setItems] = useState<Transferencia[]>([])
  const [form, setForm] = useState(emptyForm)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  const carregar = async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('mercado_transferencias')
      .select('*')
      .order('ordem', { ascending: true })
      .order('data_movimentacao', { ascending: false })

    if (error) {
      showToast('Erro ao carregar: ' + error.message, 'error')
      setItems([])
    } else {
      setItems((data || []) as Transferencia[])
    }
    setLoading(false)
  }

  useEffect(() => { carregar() }, [])

  const atualizarCampo = (campo: keyof typeof emptyForm, valor: string | boolean | number) => {
    setForm((prev) => {
      const next = { ...prev, [campo]: valor }
      if (campo === 'status') {
        const found = statusOptions.find((item) => item.label === valor)
        if (found) next.cor_status = found.color
      }
      return next
    })
  }

  const limparFormulario = () => {
    setForm(emptyForm)
    setEditingId(null)
  }

  const editar = (item: Transferencia) => {
    setEditingId(item.id)
    setForm({
      titulo: item.titulo || '',
      jogador_nome: item.jogador_nome || '',
      posicao: item.posicao || '',
      clube_origem: item.clube_origem || '',
      clube_origem_escudo: item.clube_origem_escudo || '',
      clube_destino: item.clube_destino || '',
      clube_destino_escudo: item.clube_destino_escudo || '',
      status: item.status || 'Sondagem',
      imagem_jogador: item.imagem_jogador || '',
      destaque: Boolean(item.destaque),
      ativo: item.ativo !== false,
      ordem: item.ordem || 0,
      resumo: item.resumo || '',
      link_noticia: item.link_noticia || '',
      cor_status: item.cor_status || '#3b82f6',
      data_movimentacao: item.data_movimentacao || new Date().toISOString().slice(0, 10),
    })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const salvar = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.jogador_nome.trim()) {
      showToast('Informe o nome do jogador.', 'error')
      return
    }

    setSaving(true)
    const payload = {
      titulo: form.titulo.trim() || null,
      jogador_nome: form.jogador_nome.trim(),
      posicao: form.posicao.trim() || null,
      clube_origem: form.clube_origem.trim() || null,
      clube_origem_escudo: form.clube_origem_escudo.trim() || null,
      clube_destino: form.clube_destino.trim() || null,
      clube_destino_escudo: form.clube_destino_escudo.trim() || null,
      status: form.status,
      imagem_jogador: form.imagem_jogador.trim() || null,
      destaque: form.destaque,
      ativo: form.ativo,
      ordem: Number(form.ordem) || 0,
      resumo: form.resumo.trim() || null,
      link_noticia: form.link_noticia.trim() || null,
      cor_status: form.cor_status,
      data_movimentacao: form.data_movimentacao || new Date().toISOString().slice(0, 10),
    }

    const result = editingId
      ? await supabase.from('mercado_transferencias').update(payload).eq('id', editingId)
      : await supabase.from('mercado_transferencias').insert(payload)

    setSaving(false)

    if (result.error) {
      showToast('Erro ao salvar: ' + result.error.message, 'error')
      return
    }

    showToast(editingId ? 'Transferência atualizada!' : 'Transferência cadastrada!')
    limparFormulario()
    carregar()
  }

  const excluir = async (id: number) => {
    if (!window.confirm('Excluir esta movimentação?')) return
    const { error } = await supabase.from('mercado_transferencias').delete().eq('id', id)
    if (error) {
      showToast('Erro ao excluir: ' + error.message, 'error')
      return
    }
    showToast('Transferência excluída.')
    carregar()
  }

  const toggleAtivo = async (item: Transferencia) => {
    const { error } = await supabase.from('mercado_transferencias').update({ ativo: !item.ativo }).eq('id', item.id)
    if (error) {
      showToast('Erro ao atualizar: ' + error.message, 'error')
      return
    }
    carregar()
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-alvinegro-border p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-6">
          <div>
            <h2 className="font-serif font-bold text-xl text-alvinegro-black">{editingId ? 'Editar movimentação' : 'Nova movimentação'}</h2>
            <p className="text-sm text-alvinegro-muted mt-1">Cadastre rumores, sondagens e negociações para a faixa rolante.</p>
          </div>
          {editingId && <button type="button" onClick={limparFormulario} className="px-4 py-2 rounded-full border border-alvinegro-border text-sm font-medium hover:bg-alvinegro-light transition-colors">Cancelar edição</button>}
        </div>

        <form onSubmit={salvar} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Título"><input value={form.titulo} onChange={(e) => atualizarCampo('titulo', e.target.value)} placeholder="Botafogo avança por Arthur Cabral" className="input-admin" /></Field>
            <Field label="Nome do jogador" required><input value={form.jogador_nome} onChange={(e) => atualizarCampo('jogador_nome', e.target.value)} placeholder="Arthur Cabral" className="input-admin" required /></Field>
            <Field label="Posição"><input value={form.posicao} onChange={(e) => atualizarCampo('posicao', e.target.value)} placeholder="ATA" className="input-admin" /></Field>
            <Field label="Status"><select value={form.status} onChange={(e) => atualizarCampo('status', e.target.value)} className="input-admin bg-white">{statusOptions.map((status) => <option key={status.label} value={status.label}>{status.label}</option>)}</select></Field>
            <Field label="Clube de origem"><input value={form.clube_origem} onChange={(e) => atualizarCampo('clube_origem', e.target.value)} placeholder="Benfica" className="input-admin" /></Field>
            <Field label="Escudo origem URL"><input value={form.clube_origem_escudo} onChange={(e) => atualizarCampo('clube_origem_escudo', e.target.value)} placeholder="https://..." className="input-admin" /></Field>
            <Field label="Clube de destino"><input value={form.clube_destino} onChange={(e) => atualizarCampo('clube_destino', e.target.value)} placeholder="Botafogo" className="input-admin" /></Field>
            <Field label="Escudo destino URL"><input value={form.clube_destino_escudo} onChange={(e) => atualizarCampo('clube_destino_escudo', e.target.value)} placeholder="https://..." className="input-admin" /></Field>
            <Field label="Imagem do jogador URL"><input value={form.imagem_jogador} onChange={(e) => atualizarCampo('imagem_jogador', e.target.value)} placeholder="https://..." className="input-admin" /></Field>
            <Field label="Link da notícia"><input value={form.link_noticia} onChange={(e) => atualizarCampo('link_noticia', e.target.value)} placeholder="/noticias/slug-da-noticia" className="input-admin" /></Field>
            <Field label="Ordem"><input type="number" value={form.ordem} onChange={(e) => atualizarCampo('ordem', Number(e.target.value))} className="input-admin" /></Field>
            <Field label="Data"><input type="date" value={form.data_movimentacao} onChange={(e) => atualizarCampo('data_movimentacao', e.target.value)} className="input-admin" /></Field>
            <Field label="Cor do status"><input type="color" value={form.cor_status} onChange={(e) => atualizarCampo('cor_status', e.target.value)} className="w-full h-11 rounded-lg border border-alvinegro-border" /></Field>
          </div>

          <Field label="Resumo"><textarea value={form.resumo} onChange={(e) => atualizarCampo('resumo', e.target.value)} rows={4} placeholder="Resumo rápido da negociação." className="input-admin resize-none" /></Field>

          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.destaque} onChange={(e) => atualizarCampo('destaque', e.target.checked)} />Destaque</label>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.ativo} onChange={(e) => atualizarCampo('ativo', e.target.checked)} />Ativo no ticker</label>
          </div>

          <button type="submit" disabled={saving} className="w-full md:w-auto bg-alvinegro-black text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-alvinegro-dark transition-colors disabled:opacity-50">
            {saving ? 'Salvando...' : editingId ? 'Salvar alterações' : 'Cadastrar movimentação'}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-xl border border-alvinegro-border overflow-hidden">
        <div className="p-4 border-b border-alvinegro-border flex items-center justify-between">
          <h3 className="font-semibold text-alvinegro-black">Movimentações cadastradas</h3>
          <button onClick={carregar} className="text-sm text-alvinegro-muted hover:text-alvinegro-black"><i className="ri-refresh-line mr-1"></i>Atualizar</button>
        </div>

        {loading ? <div className="p-8 text-sm text-alvinegro-muted">Carregando...</div> : items.length === 0 ? <div className="p-8 text-sm text-alvinegro-muted text-center">Nenhuma movimentação cadastrada.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="bg-alvinegro-light border-b border-alvinegro-border"><th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase">Jogador</th><th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase">Clubes</th><th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase">Status</th><th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase">Ordem</th><th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase">Ativo</th><th className="text-right px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase">Ações</th></tr></thead>
              <tbody>{items.map((item) => (
                <tr key={item.id} className="border-b border-alvinegro-border">
                  <td className="px-4 py-3"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-alvinegro-light overflow-hidden flex items-center justify-center">{item.imagem_jogador ? <img src={item.imagem_jogador} alt="" className="w-full h-full object-cover" /> : <i className="ri-user-line text-alvinegro-muted" />}</div><div><p className="text-sm font-medium text-alvinegro-black">{item.jogador_nome}</p><p className="text-xs text-alvinegro-muted">{item.posicao || '-'}</p></div></div></td>
                  <td className="px-4 py-3 text-xs text-alvinegro-muted">{item.clube_origem || '-'} → {item.clube_destino || '-'}</td>
                  <td className="px-4 py-3"><span className="px-2 py-1 rounded-full text-xs font-medium text-white" style={{ backgroundColor: item.cor_status || '#3b82f6' }}>{item.status}</span></td>
                  <td className="px-4 py-3 text-xs text-alvinegro-muted">{item.ordem || 0}</td>
                  <td className="px-4 py-3"><button type="button" onClick={() => toggleAtivo(item)} className={`px-2 py-1 rounded-full text-xs font-medium ${item.ativo ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{item.ativo ? 'Ativo' : 'Inativo'}</button></td>
                  <td className="px-4 py-3"><div className="flex justify-end gap-1"><button onClick={() => editar(item)} className="w-8 h-8 rounded-full hover:bg-alvinegro-light text-alvinegro-dark" title="Editar"><i className="ri-pencil-line" /></button><button onClick={() => excluir(item.id)} className="w-8 h-8 rounded-full hover:bg-red-50 text-red-600" title="Excluir"><i className="ri-delete-bin-line" /></button></div></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>

      {toast && <div className="fixed bottom-6 right-6 z-50"><div className={`rounded-xl px-5 py-3 shadow-lg border text-sm font-medium flex items-center gap-2 ${toast.type === 'success' ? 'bg-white border-green-200 text-green-700' : 'bg-white border-red-200 text-red-600'}`}><i className={toast.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} />{toast.message}</div></div>}

      <style>{`.input-admin{width:100%;padding:.625rem 1rem;border-radius:.5rem;border:1px solid #e5e7eb;font-size:.875rem;outline:none}.input-admin:focus{border-color:#111827}`}</style>
    </div>
  )
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return <div><label className="block text-sm font-medium text-alvinegro-dark mb-1">{label}{required && <span className="text-red-600 ml-1">*</span>}</label>{children}</div>
}
